import { TransferState } from '@/components/buttons/TransferBulk/TransferBulk.types';
import {
  TransferBulkFormSchemaType,
  transferBulkValidationSchema,
} from '@/components/buttons/TransferBulk/TransferBulkSend.utils';
import { useTransferBulk } from '@/components/buttons/TransferBulk/useTransferBulk';
import { useTokenSelection } from '@/components/features/Account/AccountNFTList/useTokenSelection';
import { TokenBeamer } from '@/constants/abis/TokenBeamer';
import { useMarketplaceChain } from '@/hooks/use-marketplace-chain';
import { useAuthentication } from '@/hooks/useAuthentication';
import { useEstimateGas } from '@/hooks/useEstimateGas';
import { wagmiConfig } from '@/lib/wagmi';
import {
  Button,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  Input,
  Text,
  chakra,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { getPaymasterData, setupZkSyncWallet } from '@sphere/reservoir-sdk';
import { waitForTransactionReceipt } from '@wagmi/core';
import useTranslation from 'next-translate/useTranslation';
import { Dispatch, SetStateAction } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { Address, encodeFunctionData } from 'viem';
import { useAccount, useSwitchChain, useWalletClient } from 'wagmi';

type Props = {
  setTransferState: Dispatch<SetStateAction<TransferState>>;
};

export const TransferBulkSend = ({ setTransferState }: Props) => {
  const { t } = useTranslation('account');
  const selectedTokens = useTokenSelection(state => state.selectedTokens);
  const marketplaceChain = useMarketplaceChain();
  const { userAddress } = useAuthentication();
  const { data: walletClient } = useWalletClient({ chainId: marketplaceChain.id });
  const { chain } = useAccount();
  const { switchChain } = useSwitchChain();

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
    setError,
  } = useForm<TransferBulkFormSchemaType>({
    mode: 'onBlur',
    resolver: zodResolver(transferBulkValidationSchema(t, userAddress)),
  });

  const params = {
    address: marketplaceChain.contracts?.tokenBeamer?.address as Address,
    abi: TokenBeamer,
    functionName: 'beamTokens',
    args: [
      [userAddress],
      selectedTokens.map(token => token.contractAddress) as Address[],
      selectedTokens.map(token => token.kind),
      selectedTokens.map(token => BigInt(token.id)),
      selectedTokens.map(token => BigInt(token.quantity)),
    ],
    chainId: marketplaceChain.id,
  };

  const { deficit: gasDeficit, showGasWarning } = useEstimateGas(params);

  const onSubmit: SubmitHandler<TransferBulkFormSchemaType> = async ({ address: toAddress }) => {
    if (!walletClient) return;

    const wallet = setupZkSyncWallet(walletClient, marketplaceChain.id);

    try {
      const hash = await wallet.sendTransaction({
        chain: marketplaceChain,
        account: userAddress!,
        to: marketplaceChain.contracts?.tokenBeamer?.address,
        data: encodeFunctionData({
          abi: TokenBeamer,
          functionName: 'beamTokens',
          args: [
            [toAddress],
            selectedTokens.map(token => token.contractAddress) as Address[],
            selectedTokens.map(token => token.kind),
            selectedTokens.map(token => BigInt(token.id)),
            selectedTokens.map(token => BigInt(token.quantity)),
          ],
        }),
        // add zkSync paymaster data if needed
        ...getPaymasterData(marketplaceChain.id),
      });

      const receipt = await waitForTransactionReceipt(wagmiConfig, {
        chainId: marketplaceChain.id,
        hash,
      });

      if (receipt.status !== 'success') throw new Error('Transaction failed');

      useTransferBulk.setState({ recipientAddress: toAddress, hash });
      setTransferState(TransferState.SUCCESS);
    } catch {
      setError('address', { message: t('assets.bulk-transfer.transfer.error') });
    }
  };

  return (
    <>
      <Heading fontSize="2xl" fontWeight="normal" mb="space.12">
        {t('assets.bulk-transfer.transfer.title')}
      </Heading>
      <Text color="gray.6" mb="space.24">
        {t('assets.bulk-transfer.transfer.description')}
      </Text>
      <chakra.form
        display="flex"
        flexDir="column"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        gap="space.20"
      >
        <FormControl isInvalid={!!errors.address}>
          <FormLabel>{t('assets.bulk-transfer.transfer.fields.address.label')}</FormLabel>
          <Input
            {...register('address')}
            type="text"
            placeholder={t('assets.bulk-transfer.transfer.fields.address.placeholder')}
          />
          <FormErrorMessage>{errors.address?.message}</FormErrorMessage>
        </FormControl>
        {showGasWarning && (
          <Flex py="space.12" px="space.18" rounded="radius.8" color="red" bg="darkRed">
            <Text fontSize="xs">
              {t('assets.bulk-transfer.transfer.gas-warning', {
                deficit: gasDeficit,
                currency: marketplaceChain.nativeCurrency.symbol,
              })}
            </Text>
          </Flex>
        )}
        {chain?.id === marketplaceChain.id ? (
          <Button type="submit" isLoading={isSubmitting}>
            {!!errors.address
              ? t('assets.bulk-transfer.transfer.cta.retry')
              : t('assets.bulk-transfer.transfer.cta.continue')}
          </Button>
        ) : (
          <Button onClick={() => switchChain?.({ chainId: marketplaceChain.id })}>
            {t('assets.bulk-transfer.transfer.cta.switch-chain', { chain: marketplaceChain.name })}
          </Button>
        )}
      </chakra.form>
    </>
  );
};
